import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";
import {
  fetchChatThread,
  sendChatMessage,
} from "../../api/ProcurementClient";
import { ChatConversation, ChatMessage } from "../../models/Chat";
import { useChat } from "./ChatProvider";
import Composer, { ComposerSubmit } from "./Composer";
import MessageItem from "./MessageItem";
import { CHAT_FAB_GUTTER, ChatDirectory, conversationTitle } from "./chatUtils";
import {
  appendMessage,
  applyReaction,
  isCompactFollowUp,
  replaceMessage,
} from "./messageActions";
import { useMessageActions } from "./useMessageActions";

interface ThreadPanelProps {
  conversation: ChatConversation;
  directory: ChatDirectory;
  threadId: number;
  onClose: () => void;
  /** Sidebar mode: show a back arrow instead of the close button. */
  backMode?: boolean;
  extraActions?: React.ReactNode;
}

const ThreadPanel: React.FC<ThreadPanelProps> = ({
  conversation,
  directory,
  threadId,
  onClose,
  backMode,
  extraActions,
}) => {
  const { me, staff, usersById } = directory;
  const { subscribe } = useChat();
  const [parent, setParent] = useState<ChatMessage | null>(null);
  const [replies, setReplies] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const thread = await fetchChatThread(threadId);
      setParent(thread.parent);
      setReplies(thread.replies);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load thread");
    }
  }, [threadId]);

  useEffect(() => {
    setParent(null);
    setReplies([]);
    setLoading(true);
    void load().finally(() => setLoading(false));
  }, [load]);

  useEffect(
    () =>
      subscribe((event) => {
        if (event.type === "message" && event.message.parentMessageId === threadId) {
          setReplies((prev) => appendMessage(prev, event.message));
        } else if (event.type === "message-updated") {
          if (event.message.id === threadId) setParent(event.message);
          else if (event.message.parentMessageId === threadId) {
            setReplies((prev) => replaceMessage(prev, event.message));
          }
        } else if (event.type === "reaction") {
          if (event.reaction.messageId === threadId) {
            setParent((prev) => (prev ? { ...prev, reactions: event.reaction.reactions } : prev));
          } else if (event.reaction.parentMessageId === threadId) {
            setReplies((prev) => applyReaction(prev, event.reaction));
          }
        } else if (event.type === "connected") {
          void load();
        }
      }),
    [subscribe, threadId, load]
  );

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [replies.length]);

  const updateAny = (updated: ChatMessage) => {
    if (updated.id === threadId) setParent(updated);
    else setReplies((prev) => replaceMessage(prev, updated));
  };

  const send = async (input: ComposerSubmit) => {
    const message = await sendChatMessage(conversation.id, { ...input, parentMessageId: threadId });
    setReplies((prev) => appendMessage(prev, message));
  };

  const actions = useMessageActions(me.id, updateAny, setError);

  const archived = Boolean(conversation.archivedAt);

  const renderMessage = (message: ChatMessage, compact: boolean) => (
    <MessageItem
      key={message.id}
      message={message}
      meId={me.id}
      usersById={usersById}
      mentionCandidates={staff}
      compact={compact}
      onToggleReaction={actions.onToggleReaction}
      onEdit={actions.onEdit}
      onDelete={actions.onDelete}
      onOpenAttachment={actions.onOpenAttachment}
    />
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderLeft: backMode ? 0 : 1,
        borderColor: "divider",
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ pl: backMode ? 0.5 : 2, pr: backMode ? 1 : 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}
      >
        {backMode && (
          <Tooltip title="Back to conversation">
            <IconButton onClick={onClose} size="small" aria-label="Back to conversation">
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Thread
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {conversationTitle(conversation, me.id, usersById)}
          </Typography>
        </Box>
        {!backMode && (
          <Tooltip title="Close thread">
            <IconButton onClick={onClose} size="small" aria-label="Close thread">
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {extraActions}
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 1.5 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : parent ? (
          <>
            {renderMessage(parent, false)}
            <Divider sx={{ my: 1.5, px: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {replies.length === 0
                  ? "No replies yet"
                  : `${replies.length} ${replies.length === 1 ? "reply" : "replies"}`}
              </Typography>
            </Divider>
            {replies.map((reply, index) => renderMessage(reply, isCompactFollowUp(replies[index - 1], reply)))}
          </>
        ) : null}
      </Box>

      {parent && !archived && (
        <Box sx={{ pl: 2, pr: CHAT_FAB_GUTTER, pb: 2, pt: 1 }}>
          <Composer
            key={threadId}
            placeholder="Reply"
            mentionCandidates={staff}
            usersById={usersById}
            onSubmit={send}
            autoFocus
            dense
          />
        </Box>
      )}

      {actions.dialogs}
    </Box>
  );
};

export default ThreadPanel;
